/// ビジネスデータとしての Email アドレス (branded 型 + 構文バリデーション)。
///
/// 対象フィールド: `firms.email` / `lawyers.email` / `contracts.billing_email` など。
/// 認証用メール (`SessionUser.email` 等、Auth0 が所有) はこの VO 対象外 — 所有権の境界で分離。
///
/// バリデーション規則は backend `Email` VO (`domain/src/core/value_objects.rs`) と揃える:
///   - 空文字 / whitespace / 制御文字を含む → null
///   - `@` が存在しない / 先頭が `@` / 末尾が `@` → null
///   - それ以外は入力文字列をそのまま保持 (正規化なし)

/** 構文検証済みのビジネスデータ Email アドレス。 */
export type Email = string & { readonly __brand: 'Email' };

/**
 * `value` を検証し、問題なければ `Email` 型で返す。
 *
 * 無効な場合は `null` を返す (例外は投げない)。
 *
 * - whitespace / 制御文字を含む場合は無効。
 * - `@` がない / 先頭が `@` / 末尾が `@` の場合は無効。
 * - RFC 詳細チェックは下流 (メールシステム / Auth0) に委譲。
 */
export function createEmail(value: string): Email | null {
  // whitespace / 制御文字チェック (backend と同規則)
  // Backend: `value.chars().any(|c| c.is_whitespace() || c.is_control())`
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0;
    // Unicode White_Space (Rust `is_whitespace()` 相当)、または制御文字 (U+0000-U+001F, U+007F-U+009F)。
    // JS の `\s` は U+FEFF を含み Rust の `is_whitespace()` より広いため `\p{White_Space}` を使う。
    if (/\p{White_Space}/u.test(ch) || code <= 0x1f || code === 0x7f || (code >= 0x80 && code <= 0x9f)) {
      return null;
    }
  }

  // `@` 構造チェック (backend: `!value.contains('@') || value.starts_with('@') || value.ends_with('@')`)
  if (!value.includes('@') || value.startsWith('@') || value.endsWith('@')) {
    return null;
  }

  return value as Email;
}
